import { useEffect, useState } from "react";
import dayjs from "dayjs";

import {
  DatepickerComponent,
  DropZoneComponent,
  SelectComponent,
  SpinnerComponent,
  TextFieldComponent,
} from "@zenith/components";
import { SelectItemsType } from "@zenith/components/ui/SelectComponent/select-component/interfaces";
import {
  BANK_VERIFICATION_NUMBER_LENGTH,
  DRIVER_LICENCE_NUMBER_LENGHT,
  DRIVER_LICENCE_REGEX,
  INTERNATIONAL_PASSPORT_NUMBER_LENGTH,
  INTERNATIONAL_PASSPORT_REGEX,
  MIN_SIZE_BACK_OFFICE,
  NATIONAL_IDENTIFICATION_NUMBER_LENGTH,
  NUMBER_INPUT_REGEX,
  ONLY_CHARACTERS_NUMBERS_AND_SPACES_REGEX,
  VOTERS_CARD_NUMBER_LENGHT,
  VOTER_IDENTITY_NUMBER_REGEX,
} from "@zenith/data/constants";
import {
  ApiResponseDTOUserDTO,
  CancelablePromise,
  UsersApiService,
} from "@zenith/services/user";
import { getMaxDateOfBirth, getMinExpiryDate } from "@zenith/utils";
import { base64toFile, directorAlreadyExists } from "../director-details.util";
import { Directors } from "../interfaces";
import {
  CitizenForm,
  FieldErrors,
  ErrorMessages,
  FieldRules,
  CitizenFormKey,
  IdentificationDocumentType,
} from "./citizen-form.model";

/* eslint-disable-next-line */
export interface CitizenFormComponentProps {
  setAddDirectorDisabled?: (state: boolean) => void;
  editFormState?: CitizenForm;
  editingMode?: boolean;
  saveCitizenForm: (form: CitizenForm) => void;
  directors?: Directors;
}

export function CitizenFormComponent(props: CitizenFormComponentProps) {
  const initialFormData: CitizenForm = {
    nin: "",
    bvn: "",
    name: "",
    date: null,
    identificationDocument: "",
    passportExpiryDate: null,
    passportNumber: "",
    passportDocument: "",
    driverLicenceNumber: "",
    driverLicenceExpiryDate: null,
    driverLicenceFront: "",
    driverLicenceBack: "",
    votersNumber: "",
    votersCardFront: "",
    votersCardBack: "",
    nationalIdCardFront: "",
    nationalIdCardBack: "",
  };
  const initialFormDataErrors = Object.keys(initialFormData).reduce(
    (acc, key) => {
      Object.assign(acc, {
        [key]: {
          hasError: false,
          message: "",
        },
      });
      return acc;
    },
    {} as FieldErrors
  );

  const identificationDocument: SelectItemsType[] = [
    {
      label: "Driver´s License",
      value: IdentificationDocumentType.DriversLicense,
    },
    {
      label: "International Passport",
      value: IdentificationDocumentType.InternationalPassport,
    },
    {
      label: "Voter´s Card",
      value: IdentificationDocumentType.VotersCard,
    },
    {
      label: "National identification number (NIN/NIMC)",
      value: IdentificationDocumentType.NationalIdCard,
    },
  ];

  const formErrorMessages: ErrorMessages = {
    nin: {
      required: "National identification number (NIN/NIMC) is required",
      length: "National identification number (NIN/NIMC) not valid",
    },
    bvn: {
      required: "BVN is required",
      length: "BVN not valid",
    },
    identificationDocument: {
      required: "Identification document is required",
    },
    date: {
      required: "Date of birth is required",
      invalid: "BVN details mismatch",
    },
    passportExpiryDate: {
      required: "Expiry date is required",
    },
    passportNumber: {
      required: "Passport number is required",
      invalid: "Passport number not valid",
    },
    driverLicenceNumber: {
      required: "Driver's licence number is required",
      invalid: "Driver's licence number is not valid",
    },
    driverLicenceExpiryDate: {
      required: "Expiry date is required",
    },
    votersNumber: {
      required: "Voter identity number is required",
      invalid: "Voter identity number not valid",
    },
  };

  const formFieldChangeRules: FieldRules = {
    nin: NUMBER_INPUT_REGEX,
    bvn: NUMBER_INPUT_REGEX,
    driverLicenceNumber: ONLY_CHARACTERS_NUMBERS_AND_SPACES_REGEX,
    votersNumber: ONLY_CHARACTERS_NUMBERS_AND_SPACES_REGEX,
    passportNumber: ONLY_CHARACTERS_NUMBERS_AND_SPACES_REGEX,
  };

  const formFieldBlurRules: FieldRules = {
    passportNumber: INTERNATIONAL_PASSPORT_REGEX,
    driverLicenceNumber: DRIVER_LICENCE_REGEX,
    votersNumber: VOTER_IDENTITY_NUMBER_REGEX,
  };

  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [calendarOpen, setCalendarOpen] = useState<boolean>(false);
  const [isBVNLoading, setBVNLoading] = useState<boolean>(false);
  const [passportExpiryDateOpen, setPassportExpiryDateOpen] =
    useState<boolean>(false);
  const [driverLicenceExpiryDateOpen, setDriverLicenceExpiryDateOpen] =
    useState<boolean>(false);
  const [formData, setFormData] = useState<CitizenForm>(
    props.editFormState ? props.editFormState : initialFormData
  );
  const [formDataErrors, setFormDataErrors] = useState<FieldErrors>(
    initialFormDataErrors
  );
  const [bvnName, setBvnName] = useState<string | undefined>(undefined);
  const [ongoingVerifyBvn, setOngoingVerifyBvn] = useState<
    CancelablePromise<ApiResponseDTOUserDTO> | undefined
  >(undefined);
  const [uploadedDriverLicenceFront, setUploadedDriverLicenceFront] = useState<
    File | undefined
  >(
    props.editFormState
      ? base64toFile(
          props.editFormState.driverLicenceFront,
          "drivers-licence-front"
        )
      : undefined
  );
  const [uploadedDriverLicenceBack, setUploadedDriverLicenceBack] = useState<
    File | undefined
  >(
    props.editFormState
      ? base64toFile(
          props.editFormState.driverLicenceBack,
          "drivers-licence-back"
        )
      : undefined
  );
  const [uploadedNationalIdCardFront, setUploadedNationalIdCardFront] =
    useState<File | undefined>(
      props.editFormState
        ? base64toFile(
            props.editFormState.nationalIdCardFront,
            "national-id-card-front"
          )
        : undefined
    );
  const [uploadedNationalIdCardBack, setUploadedNationalIdCardBack] = useState<
    File | undefined
  >(
    props.editFormState
      ? base64toFile(
          props.editFormState.nationalIdCardBack,
          "national-id-card-back"
        )
      : undefined
  );
  const [uploadedPassportDocument, setUploadedPassportDocument] = useState<
    File | undefined
  >(
    props.editFormState
      ? base64toFile(props.editFormState.passportDocument, "passport-document")
      : undefined
  );
  const [uploadedVotersCardFront, setUploadedVotersCardFront] = useState<
    File | undefined
  >(
    props.editFormState
      ? base64toFile(props.editFormState.votersCardFront, "voters-card-front")
      : undefined
  );
  const [uploadedVotersCardBack, setUploadedVotersCardBack] = useState<
    File | undefined
  >(
    props.editFormState
      ? base64toFile(props.editFormState.votersCardBack, "voters-card-back")
      : undefined
  );

  const resetUploadedDocuments = () => {
    setUploadedDriverLicenceFront(undefined);
    setUploadedDriverLicenceBack(undefined);
    setUploadedNationalIdCardFront(undefined);
    setUploadedNationalIdCardBack(undefined);
    setUploadedPassportDocument(undefined);
    setUploadedVotersCardFront(undefined);
    setUploadedVotersCardBack(undefined);
  };

  const resetIdentificationDocumentFields = () => {
    setFormData({
      ...formData,
      driverLicenceFront: "",
      driverLicenceBack: "",
      driverLicenceExpiryDate: null,
      driverLicenceNumber: "",
      passportDocument: "",
      passportExpiryDate: null,
      passportNumber: "",
      nationalIdCardFront: "",
      nationalIdCardBack: "",
      votersCardBack: "",
      votersCardFront: "",
      votersNumber: "",
    });
    resetUploadedDocuments();
  };

  const isDriversLicenceFilled = () => {
    return !!(
      formData.driverLicenceBack &&
      formData.driverLicenceFront &&
      formData.driverLicenceExpiryDate &&
      formData.driverLicenceNumber
    );
  };

  const isInternationalPassportFilled = () => {
    return !!(
      formData.passportDocument &&
      formData.passportExpiryDate &&
      formData.passportNumber
    );
  };
  const isNationalIdCardFilled = () => {
    return !!(formData.nationalIdCardFront && formData.nationalIdCardBack);
  };
  const isVotersCardFilled = () => {
    return !!(
      formData.votersCardFront &&
      formData.votersCardBack &&
      formData.votersNumber
    );
  };

  useEffect(() => {
    const requiredFormKeys: CitizenFormKey[] = [
      "bvn",
      "name",
      "date",
      "identificationDocument",
      "nin",
    ];
    const hasError = Object.values(formDataErrors).some(
      (item) => item.hasError
    );
    const isFilled = Object.entries(formData)
      .filter(([key]) => requiredFormKeys.includes(key as CitizenFormKey))
      .every(([_, value]) => {
        switch (value) {
          case IdentificationDocumentType.DriversLicense:
            return isDriversLicenceFilled();
          case IdentificationDocumentType.InternationalPassport:
            return isInternationalPassportFilled();
          case IdentificationDocumentType.NationalIdCard:
            return isNationalIdCardFilled();
          case IdentificationDocumentType.VotersCard:
            return isVotersCardFilled();
          default:
            return !!value;
        }
      });
    props.saveCitizenForm(formData);

    if (props.setAddDirectorDisabled) {
      props.setAddDirectorDisabled(!isFilled || hasError);
    }
  }, [formDataErrors, formData]);

  const applyRule = (rule: RegExp | undefined, value: string): string => {
    if (!rule) {
      return value;
    }
    return value.replace(rule, "");
  };

  const onInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: CitizenFormKey
  ) => {
    event.currentTarget.value = applyRule(
      formFieldChangeRules[field],
      event.currentTarget.value
    );
    onFieldChange(event.currentTarget.value, field);
  };

  const onDateFieldChange = (date: any, field: CitizenFormKey): void => {
    onFieldChange(date, field);
  };

  const onSelectChange = (
    value: string | null,
    field: CitizenFormKey
  ): void => {
    if (!value) {
      return;
    }
    onFieldChange(value, field);
  };

  const onFieldChange = (value: string, field: CitizenFormKey) => {
    setFormDataErrors({
      ...formDataErrors,
      [field]: {
        hasError: false,
        message: "",
      },
    });
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const onInputBlur = (
    event: React.FocusEvent<HTMLInputElement, Element>,
    field: CitizenFormKey
  ) => {
    const value = event.currentTarget.value;
    const fieldLength = event.currentTarget.getAttribute("maxlength");
    onFieldBlur(value, field, fieldLength);
  };
  const onSelectBlur = (value: string, field: CitizenFormKey) => {
    onFieldBlur(value, field);
  };

  const onFieldBlur = (
    value: string,
    field: CitizenFormKey,
    maxLength?: string | null
  ) => {
    const requiredMessage = formErrorMessages[field]?.required;
    if (value === "" && requiredMessage) {
      setFormDataErrors({
        ...formDataErrors,
        [field]: {
          hasError: true,
          message: requiredMessage,
        },
      });
      return;
    }
    const lengthMessage = formErrorMessages[field]?.length;

    if (maxLength && value.length < Number(maxLength) && lengthMessage) {
      setFormDataErrors({
        ...formDataErrors,
        [field]: {
          hasError: true,
          message: lengthMessage,
        },
      });
      return;
    }
    const invalidMessage = formErrorMessages[field]?.invalid;
    if (
      formFieldBlurRules[field] &&
      !formFieldBlurRules[field]?.test(value) &&
      invalidMessage
    ) {
      setFormDataErrors({
        ...formDataErrors,
        [field]: {
          hasError: true,
          message: invalidMessage,
        },
      });
      return;
    }
  };

  // Combine first and last name to create director name
  const getDirectorName = (firstName: string, lastName: string): string => {
    const fullName = [firstName, lastName].filter(Boolean).join(" ");
    return fullName.trim();
  };

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }
    resetIdentificationDocumentFields();
  }, [formData.identificationDocument]);

  useEffect(() => {
    if (bvnName !== undefined) {
      setFormData({
        ...formData,
        name: bvnName,
      });
    }
  }, [bvnName]);

  useEffect(() => {
    if (ongoingVerifyBvn) {
      ongoingVerifyBvn.cancel();
      setOngoingVerifyBvn(undefined);
    }
    setBvnName("");
    setFormDataErrors({
      ...formDataErrors,
      date: {
        hasError: false,
        message: "",
      },
    });

    if (
      formData.date &&
      formData.bvn?.length === BANK_VERIFICATION_NUMBER_LENGTH &&
      !formDataErrors.bvn.hasError
    ) {
      if (props.editingMode) {
        return;
      }
      if (
        props.directors &&
        directorAlreadyExists(props.directors, formData.bvn)
      ) {
        setFormDataErrors({
          ...formDataErrors,
          bvn: {
            hasError: true,
            message: "BVN already exists in Director’s list",
          },
        });
        return;
      }
      setBVNLoading(true);
      const bankVerificationNumber = formData.bvn;
      const verifyBvnRequest = UsersApiService.verifyBvn({
        bankVerificationNumber,
      });
      verifyBvnRequest
        .then((response: ApiResponseDTOUserDTO) => {
          const directorName = getDirectorName(
            response.data?.firstName ?? "",
            response.data?.lastName ?? ""
          );
          const birthDate = response.data?.birthdate;
          const formBvnDate = formData.date?.format("YYYY-MM-DD");
          if (birthDate === formBvnDate) {
            setBvnName(directorName);
          } else {
            setFormDataErrors({
              ...formDataErrors,
              date: {
                hasError: true,
                message: formErrorMessages.date?.invalid || "",
              },
            });
          }
          setBVNLoading(false);
        })
        .catch((error) => {
          if (error.name === "CancelError") return;
          setBVNLoading(false);
          setFormDataErrors({
            ...formDataErrors,
            bvn: {
              hasError: true,
              message: formErrorMessages.bvn?.length || "",
            },
          });
          return;
        });
      setOngoingVerifyBvn(verifyBvnRequest);
    }
  }, [formDataErrors.bvn.hasError, formData.bvn, formData.date]);

  const getImage = (image: File | null, field: CitizenFormKey) => {
    const reader = new FileReader();
    if (!image) return;
    reader.readAsDataURL(image);
    reader.onload = function () {
      setFormData({
        ...formData,
        [field]: reader.result,
      });
    };
  };

  const deleteImage = (field: CitizenFormKey) => {
    setFormData({
      ...formData,
      [field]: "",
    });
  };

  const driverLicenceDocForm = () => {
    return (
      <>
        <div className="mb-5 mt-2">
          <TextFieldComponent
            id="driver-licence-field"
            labelId="driver-licence-field-label"
            className="grow"
            containerClassName="flex grow"
            maxLength={DRIVER_LICENCE_NUMBER_LENGHT}
            hasError={formDataErrors.driverLicenceNumber.hasError}
            label="Driver's Licence Number"
            placeholder="Enter driver's licence number"
            type="text"
            value={formData.driverLicenceNumber}
            onChange={(event) => onInputChange(event, "driverLicenceNumber")}
            onBlur={(event) => onInputBlur(event, "driverLicenceNumber")}
          />
          {formDataErrors.driverLicenceNumber.hasError && (
            <div className="mt-1 ml-2 text-primary-250">
              <span className="break-words text-xs">
                {formDataErrors.driverLicenceNumber.message}
              </span>
            </div>
          )}
        </div>
        <div className="mb-5">
          <DatepickerComponent
            onOpen={() => setDriverLicenceExpiryDateOpen(true)}
            onClose={() => {
              setDriverLicenceExpiryDateOpen(false);
            }}
            id="driver-licence-expiry-date"
            labelId="driver-licence-expiry-date-label"
            className="[&>label]:text-main-200-50 w-full"
            format="DD/MM/YYYY"
            label="Expiry Date"
            minDate={getMinExpiryDate()}
            formatDensity="spacious"
            isOpen={driverLicenceExpiryDateOpen}
            showCalendarIcon={false}
            disableOpenPicker={false}
            hasError={formDataErrors.driverLicenceExpiryDate.hasError}
            value={formData.driverLicenceExpiryDate}
            onChange={(event) =>
              onDateFieldChange(event, "driverLicenceExpiryDate")
            }
            validationMessage={""}
            slotProps={{
              field: {
                onBlur: () =>
                  onFieldBlur(
                    formData.driverLicenceExpiryDate?.isValid()
                      ? formData.driverLicenceExpiryDate.toISOString()
                      : "",
                    "driverLicenceExpiryDate"
                  ),
              },
              textField: {
                placeholder: "Select a date",
                onKeyDown: (event) => event.preventDefault(),
                onClick: () => {
                  setDriverLicenceExpiryDateOpen(!driverLicenceExpiryDateOpen);
                },
                focused: false,
              },
            }}
          />
          {formDataErrors.driverLicenceExpiryDate.hasError && (
            <div className="mt-1 ml-2 text-primary-250">
              <span className="break-words text-xs">
                {formDataErrors.driverLicenceExpiryDate.message}
              </span>
            </div>
          )}
        </div>
        <div className="mb-8">
          <h4 className="mb-1 text-lg font-bold">
            Upload Identification Document
          </h4>
          <p className="text-sm font-extralight text-neutral-400">
            Maximum file size is 10MB, and the file type must be JPEG. Ensure
            the document is still valid.
          </p>
        </div>
        <div className="mb-6 cursor-pointer">
          <DropZoneComponent
            fieldName={"Driver's Licence - Front"}
            file={uploadedDriverLicenceFront}
            getImage={(image: File | null) =>
              getImage(image, "driverLicenceFront")
            }
            deleteImage={() => deleteImage("driverLicenceFront")}
            parentFileChangeListener={formData.identificationDocument}
            acceptedMinSize={MIN_SIZE_BACK_OFFICE}
          />
        </div>
        <div className="mb-6 cursor-pointer">
          <DropZoneComponent
            fieldName={"Driver's Licence - Back"}
            file={uploadedDriverLicenceBack}
            getImage={(image: File | null) =>
              getImage(image, "driverLicenceBack")
            }
            deleteImage={() => deleteImage("driverLicenceBack")}
            parentFileChangeListener={formData.identificationDocument}
            acceptedMinSize={MIN_SIZE_BACK_OFFICE}
          />
        </div>
      </>
    );
  };

  const internationPassportDocForm = () => {
    return (
      <>
        <div className="mb-5 mt-2">
          <TextFieldComponent
            id="passport-number-field"
            labelId="passport-number-field-label"
            className="grow"
            containerClassName="flex grow"
            maxLength={INTERNATIONAL_PASSPORT_NUMBER_LENGTH}
            hasError={formDataErrors.passportNumber.hasError}
            label="Passport Number"
            placeholder="Enter passport number"
            type="text"
            value={formData.passportNumber}
            onChange={(event) => onInputChange(event, "passportNumber")}
            onBlur={(event) => onInputBlur(event, "passportNumber")}
          />
          {formDataErrors.passportNumber.hasError && (
            <div className="mt-1 ml-2 text-primary-250">
              <span className="break-words text-xs">
                {formDataErrors.passportNumber.message}
              </span>
            </div>
          )}
        </div>
        <div className="mb-5">
          <DatepickerComponent
            onOpen={() => setPassportExpiryDateOpen(true)}
            onClose={() => {
              setPassportExpiryDateOpen(false);
            }}
            id="passport-expiry-date"
            labelId="passport-expiry-date-label"
            className="[&>label]:text-main-200-50 w-full"
            format="DD/MM/YYYY"
            label="Expiry Date"
            formatDensity="spacious"
            minDate={getMinExpiryDate()}
            isOpen={passportExpiryDateOpen}
            showCalendarIcon={false}
            disableOpenPicker={false}
            hasError={formDataErrors.passportExpiryDate.hasError}
            value={formData.passportExpiryDate}
            onChange={(event) => onDateFieldChange(event, "passportExpiryDate")}
            validationMessage={""}
            slotProps={{
              field: {
                onBlur: () =>
                  onFieldBlur(
                    formData.passportExpiryDate?.isValid()
                      ? formData.passportExpiryDate.toISOString()
                      : "",
                    "passportExpiryDate"
                  ),
              },
              textField: {
                placeholder: "Select a date",
                onKeyDown: (event) => event.preventDefault(),
                onClick: () => {
                  setPassportExpiryDateOpen(!passportExpiryDateOpen);
                },
                focused: false,
              },
            }}
          />
          {formDataErrors.passportExpiryDate.hasError && (
            <div className="mt-1 ml-2 text-primary-250">
              <span className="break-words text-xs">
                {formDataErrors.passportExpiryDate.message}
              </span>
            </div>
          )}
        </div>
        <div className="mb-8">
          <h4 className="mb-1 text-lg font-bold">
            Upload Identification Document
          </h4>
          <p className="text-sm font-extralight text-neutral-400">
            Maximum file size is 10MB, and the file type must be JPEG. Ensure
            the document is still valid.
          </p>
        </div>
        <div className="mb-6 cursor-pointer">
          <DropZoneComponent
            fieldName={"Passport Bio Page"}
            file={uploadedPassportDocument}
            getImage={(image: File | null) =>
              getImage(image, "passportDocument")
            }
            deleteImage={() => deleteImage("passportDocument")}
            parentFileChangeListener={formData.identificationDocument}
            acceptedMinSize={MIN_SIZE_BACK_OFFICE}
          />
        </div>
      </>
    );
  };

  const nationalIDCardDocForm = () => {
    return (
      <>
        <div className="mb-8">
          <h4 className="mb-1 text-lg font-bold">
            Upload Identification Document
          </h4>
          <p className="text-sm font-extralight text-neutral-400">
            Maximum file size is 10MB, and the file type must be JPEG. Ensure
            the document is still valid.
          </p>
        </div>
        <div className="mb-6 cursor-pointer">
          <DropZoneComponent
            fieldName={"National ID Card - Front"}
            file={uploadedNationalIdCardFront}
            getImage={(image: File | null) =>
              getImage(image, "nationalIdCardFront")
            }
            deleteImage={() => deleteImage("nationalIdCardFront")}
            parentFileChangeListener={formData.identificationDocument}
            acceptedMinSize={MIN_SIZE_BACK_OFFICE}
          />
        </div>
        <div className="mb-6 cursor-pointer">
          <DropZoneComponent
            fieldName={"National ID Card - Back"}
            file={uploadedNationalIdCardBack}
            getImage={(image: File | null) =>
              getImage(image, "nationalIdCardBack")
            }
            deleteImage={() => deleteImage("nationalIdCardBack")}
            parentFileChangeListener={formData.identificationDocument}
            acceptedMinSize={MIN_SIZE_BACK_OFFICE}
          />
        </div>
      </>
    );
  };

  const votersCardDocForm = () => {
    return (
      <>
        <div className="mb-5 mt-2">
          <TextFieldComponent
            id="voters-card-number-field"
            labelId="voters-card-number-field-label"
            className="grow"
            containerClassName="flex grow"
            maxLength={VOTERS_CARD_NUMBER_LENGHT}
            hasError={formDataErrors.votersNumber.hasError}
            label="Voter Identity Number (VIN)"
            placeholder="Enter voter identity number"
            type="text"
            value={formData.votersNumber}
            onChange={(event) => onInputChange(event, "votersNumber")}
            onBlur={(event) => onInputBlur(event, "votersNumber")}
          />
          {formDataErrors.votersNumber.hasError && (
            <div className="mt-1 ml-2 text-primary-250">
              <span className="break-words text-xs">
                {formDataErrors.votersNumber.message}
              </span>
            </div>
          )}
        </div>
        <div className="mb-8">
          <h4 className="mb-1 text-lg font-bold">
            Upload Identification Document
          </h4>
          <p className="text-sm font-extralight text-neutral-400">
            Maximum file size is 10MB, and the file type must be JPEG. Ensure
            the document is still valid.
          </p>
        </div>
        <div className="mb-6 cursor-pointer">
          <DropZoneComponent
            fieldName={"Voter's Card - Front"}
            file={uploadedVotersCardFront}
            getImage={(image: File | null) =>
              getImage(image, "votersCardFront")
            }
            deleteImage={() => deleteImage("votersCardFront")}
            parentFileChangeListener={formData.identificationDocument}
            acceptedMinSize={MIN_SIZE_BACK_OFFICE}
          />
        </div>
        <div className="mb-6 cursor-pointer">
          <DropZoneComponent
            fieldName={"Voter's Card - Back"}
            file={uploadedVotersCardBack}
            getImage={(image: File | null) => getImage(image, "votersCardBack")}
            deleteImage={() => deleteImage("votersCardBack")}
            parentFileChangeListener={formData.identificationDocument}
            acceptedMinSize={MIN_SIZE_BACK_OFFICE}
          />
        </div>
      </>
    );
  };

  const requiredIdentificationDocuments = (
    identificationDocumentType: IdentificationDocumentType
  ) => {
    switch (identificationDocumentType) {
      case IdentificationDocumentType.DriversLicense:
        return driverLicenceDocForm();
      case IdentificationDocumentType.InternationalPassport:
        return internationPassportDocForm();
      case IdentificationDocumentType.NationalIdCard:
        return nationalIDCardDocForm();
      case IdentificationDocumentType.VotersCard:
        return votersCardDocForm();
      default:
        return <></>;
    }
  };

  return (
    <div>
      <div className="mb-5 mt-2">
        <TextFieldComponent
          id="nin-field"
          labelId="nin-field-label"
          className="grow"
          containerClassName="flex grow"
          maxLength={NATIONAL_IDENTIFICATION_NUMBER_LENGTH}
          hasError={formDataErrors.nin.hasError}
          label="National Identification Number (NIN/NIMC)"
          placeholder="Enter national identification number"
          type="tel"
          value={formData.nin}
          onChange={(event) => onInputChange(event, "nin")}
          onBlur={(event) => onInputBlur(event, "nin")}
        />
        {formDataErrors.nin.hasError && (
          <div className="mt-1 ml-2 text-primary-250">
            <span className="break-words text-xs">
              {formDataErrors.nin.message}
            </span>
          </div>
        )}
      </div>
      <div className="mb-5">
        <TextFieldComponent
          id="bvn-field"
          labelId="bvn-field-label"
          className="grow"
          containerClassName="flex grow"
          maxLength={BANK_VERIFICATION_NUMBER_LENGTH}
          hasError={formDataErrors.bvn.hasError}
          label="Bank Verification Number (BVN)"
          placeholder="Enter bank verification number"
          type="tel"
          value={formData.bvn}
          disabled={!!props.editingMode}
          onChange={(event) => onInputChange(event, "bvn")}
          onBlur={(event) => onInputBlur(event, "bvn")}
        />
        {formDataErrors.bvn.hasError && (
          <div className="mt-1 ml-2 text-primary-250">
            <span className="break-words text-xs">
              {formDataErrors.bvn.message}
            </span>
          </div>
        )}
      </div>
      <div className="mb-5">
        <DatepickerComponent
          onOpen={() => setCalendarOpen(true)}
          onClose={() => {
            setCalendarOpen(false);
          }}
          id="director-date-of-birth"
          labelId="director-date-of-birth-label"
          className="[&>label]:text-main-200-50 w-full"
          format="DD/MM/YYYY"
          label="Date of Birth"
          maxDate={getMaxDateOfBirth()}
          formatDensity="spacious"
          isOpen={calendarOpen}
          showCalendarIcon={false}
          disableOpenPicker={false}
          hasError={formDataErrors.date.hasError}
          value={formData.date}
          disabled={!!props.editingMode}
          onChange={(event) => onDateFieldChange(event, "date")}
          validationMessage={""}
          slotProps={{
            field: {
              onBlur: () =>
                onFieldBlur(
                  formData.date?.isValid() ? formData.date.toISOString() : "",
                  "date"
                ),
            },
            textField: {
              placeholder: "Select a date",
              onKeyDown: (event) => event.preventDefault(),
              onClick: () => {
                setCalendarOpen(!calendarOpen);
              },
              focused: false,
            },
          }}
        />
        {formDataErrors.date.hasError && (
          <div className="mt-1 ml-2 text-primary-250">
            <span className="break-words text-xs">
              {formDataErrors.date.message}
            </span>
          </div>
        )}
      </div>
      <div className="mb-8">
        <TextFieldComponent
          id="bvn-name-field"
          labelId="bvn-name-label"
          className="grow"
          containerClassName="flex grow"
          label="Director's Name"
          placeholder="Director's name"
          type="tel"
          value={formData.name}
          onChange={(event) => onInputChange(event, "name")}
          disabled={true}
          endDecorator={
            isBVNLoading ? (
              <SpinnerComponent size={25} type={"primary"} showIcon={false} />
            ) : (
              ""
            )
          }
        />
      </div>
      <div className="mb-5">
        <SelectComponent
          items={identificationDocument}
          className="w-auto"
          label="Identification Document"
          id="identificationDocument"
          labelId="identificationDocumentLabel"
          value={formData.identificationDocument}
          defaultValue={""}
          listboxZIndex={1500}
          placeholder="Select identification document"
          hasError={formDataErrors.identificationDocument.hasError}
          onChange={(_, newValue) =>
            onSelectChange(newValue, "identificationDocument")
          }
          onClose={(newValue) =>
            onSelectBlur(newValue, "identificationDocument")
          }
        />
        {formDataErrors.identificationDocument.hasError && (
          <div className="mt-1 ml-2 text-primary-250">
            <span className="break-words text-xs">
              {formDataErrors.identificationDocument.message}
            </span>
          </div>
        )}
      </div>

      {requiredIdentificationDocuments(
        formData.identificationDocument as IdentificationDocumentType
      )}
    </div>
  );
}

export default CitizenFormComponent;
